import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Linkify from 'react-linkify';
import { API, getAuthToken, isAuthenticated } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { AdSlot } from '../components/ads/AdSlot';
import { 
  Briefcase, Building2, MapPin, DollarSign, Link2, 
  Send, ArrowLeft, Clock, Users, CheckCircle2,
  Facebook, Twitter, Linkedin, Share2, MessageCircle
} from 'lucide-react';

const PublicJobView = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/public/${jobId}`);
      setJob(response.data);
    } catch (error) {
      setError('Job not found or has been removed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (isAuthenticated()) {
      navigate('/candidate');
    } else {
      // Store job ID to redirect after login
      localStorage.setItem('applyToJob', jobId);
      navigate('/login');
    }
  };

  const getShareUrl = () => {
    return window.location.href;
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const text = `Check out this job: ${job?.position_name} at ${job?.company_name}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(getShareUrl())}`, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = () => {
    const text = `Check out this job opportunity: ${job?.position_name} at ${job?.company_name} - ${getShareUrl()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareToTelegram = () => {
    const text = `Check out this job opportunity: ${job?.position_name} at ${job?.company_name}`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const copyLink = () => {
    const url = getShareUrl();
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      alert('Link copied to clipboard!');
    } catch (e) {
      alert('Could not copy link');
    }
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-100 to-purple-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-100 to-purple-100">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Briefcase className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Job Not Found</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} className="bg-violet-600 hover:bg-violet-700">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white shadow-xl">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png?v=15" alt="Logo" className="h-10 w-10 rounded-xl" />
            <div>
              <h1 className="font-heading text-xl font-bold">Officium Connect</h1>
              <p className="text-xs text-white/70">Job Opportunity</p>
            </div>
          </div>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => navigate('/login')}
            className="bg-white/20 text-white hover:bg-white/30 border-0"
          >
            Login / Sign Up
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="border-0 shadow-2xl overflow-hidden">
          {/* Job Header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-6 text-white">
            <div className="flex items-start gap-4">
              {job.company_logo_url ? (
                <img 
                  src={`${API.replace('/api', '')}${job.company_logo_url}`}
                  alt={job.company_name}
                  className="h-20 w-20 rounded-2xl object-cover border-4 border-white/30 shadow-lg"
                />
              ) : (
                <div className="h-20 w-20 rounded-2xl bg-white/20 flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                  {job.company_name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">{job.company_name}</h1>
                <p className="flex items-center gap-2 text-white/80 text-lg">
                  <Building2 className="h-5 w-5" />
                  {job.position_name || 'Multiple Positions Available'}
                </p>
              </div>
            </div>
          </div>

          {/* Job Image */}
          {job.job_image_url && (
            <img 
              src={`${API.replace('/api', '')}${job.job_image_url}`}
              alt="Job"
              className="w-full h-64 object-cover"
            />
          )}

          <CardContent className="p-6 space-y-6">
            {/* Key Info */}
            <div className="flex flex-wrap gap-3">
              {job.location && (
                <Badge variant="secondary" className="bg-violet-100 text-violet-700 px-4 py-2 text-sm">
                  <MapPin className="h-4 w-4 mr-2" />
                  {job.location}
                </Badge>
              )}
              {job.salary_range && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 px-4 py-2 text-sm">
                  <DollarSign className="h-4 w-4 mr-2" />
                  {job.salary_range}
                </Badge>
              )}
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 px-4 py-2 text-sm">
                <Clock className="h-4 w-4 mr-2" />
                Posted {new Date(job.created_at).toLocaleDateString()}
              </Badge>
            </div>

            {/* Description */}
            <div>
              <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-violet-600" />
                Job Description
              </h2>
              <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {decoratedText}
                </a>
              )}>
                <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">{job.job_description}</p>
              </Linkify>
            </div>

            {/* Requirements */}
            {job.requirements && (
              <div>
                <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-violet-600" />
                  Requirements
                </h2>
                <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {decoratedText}
                  </a>
                )}>
                  <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">{job.requirements}</p>
                </Linkify>
              </div>
            )}

            {/* Sponsored - after the job content, deliberately above the Apply divider */}
            <AdSlot slot="detail" enoughContent={(job.job_description || '').length > 400} label="Sponsored" />

            {/* Career Site Link */}            {job.career_site_link && (
              <a 
                href={job.career_site_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 font-medium"
              >
                <Link2 className="h-5 w-5" />
                View on Company Career Site
              </a>
            )}

            {/* Apply Button */}
            <div className="pt-6 border-t">
              <Button 
                onClick={handleApply}
                className="w-full py-6 text-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg"
              >
                <Send className="h-5 w-5 mr-2" />
                Apply for this Position
              </Button>
              <p className="text-center text-sm text-gray-500 mt-3">
                {isAuthenticated() ? 'Click to apply now' : 'Login or create an account to apply'}
              </p>
            </div>

            {/* Social Share */}
            <div className="pt-6 border-t">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Share2 className="h-4 w-4" />
                Share this Job
              </h3>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={shareToFacebook}
                  className="bg-[#1877F2] text-white hover:bg-[#166FE5] border-0"
                >
                  <Facebook className="h-4 w-4 mr-1" />
                  Facebook
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={shareToTwitter}
                  className="bg-[#1DA1F2] text-white hover:bg-[#1A91DA] border-0"
                >
                  <Twitter className="h-4 w-4 mr-1" />
                  Twitter
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={shareToLinkedIn}
                  className="bg-[#0A66C2] text-white hover:bg-[#004182] border-0"
                >
                  <Linkedin className="h-4 w-4 mr-1" />
                  LinkedIn
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={shareToWhatsApp}
                  className="bg-[#25D366] text-white hover:bg-[#128C7E] border-0"
                >
                  <MessageCircle className="h-4 w-4 mr-1" />
                  WhatsApp
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={shareToTelegram}
                  className="bg-[#0088cc] text-white hover:bg-[#006699] border-0"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Telegram
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyLink}
                  className="bg-gray-600 text-white hover:bg-gray-700 border-0"
                >
                  <Link2 className="h-4 w-4 mr-1" />
                  Copy Link
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Powered by Officium Connect</p>
          <p className="mt-1">Complete solution to embrace your capability</p>
        </div>
      </main>
    </div>
  );
};

export default PublicJobView;
